import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslateFunction } from '../helpers/useTranslationHook';

import { Favorite } from '../components/Favorite';
import { SidebarPanel } from '../components/Sidebar/SidebarPanel';

import { useMap } from '../Map';
import { useStore } from '../stores';
import { Layers, Modals } from '../stores/layers';
import { flyToPoi } from '../helpers/mapHelper';

export const Favorites = observer(() => {
  const { t } = useTranslateFunction([
    'gys-' + Layers.favourites,
    'gys-' + Layers.myLocations,
  ]);

  const map = useMap();

  const { favourites, layers, poi, config, myLocations } = useStore();

  const flyTo = (target) => {
    if (!target) return;
    layers.closeLayer();

    flyToPoi(map, target, poi);

    poi.setActive(target);
    layers.openLayer(Layers.info);
  };

  return (
    <SidebarPanel
      isOpen={layers.activeLayer === Layers.favourites}
      title={t('gys-favourites:headline')}
      closeAction={() => {
        favourites.setSelectable(false);
        layers.closeLayer();
      }}
      options={
        favourites?.list?.length &&
        (!favourites.selectable || !myLocations.selectable) ? (
          <button
            id="delete-button"
            className="bg-gray-300 py-1 px-3 absolute top-1/2 right-0 -translate-y-1/2 text-sm rounded-3xl hover:bg-gray-400 transition-all"
            onClick={() => {
              favourites.setSelectable(true);
              favourites.deselectAll();
              myLocations.setSelectable(true);
              myLocations.deselectAll();
            }}
          >
            {t('gys-favourites:button_delete')}
          </button>
        ) : (
          <span className="text-sm ml-2">{`${favourites.selected.length} ${t(
            'gys-favourites:pois_selected',
          )}`}</span>
        )
      }
    >
      <div className="h-full mt-3 pb-12 px-6 overflow-auto">
        {favourites?.list?.length
          ? favourites.list.map((item) => (
              <Favorite
                poi={item}
                language={config.gysService.language}
                key={item.id}
                onClick={flyTo.bind(null, item)}
                selectable={favourites.selectable}
                selected={
                  favourites.selected.filter((fav) => fav.id === item.id)
                    .length > 0
                }
                onSeletionChanged={(event) => {
                  if (event.target.checked) {
                    favourites.select(item);
                  } else {
                    favourites.deselect(item);
                  }
                }}
              />
            ))
          : null}

        {favourites.selectable ? (
          <div className="bg-gray-100 p-3 flex justify-between fixed bottom-0 left-0 right-0 z-10">
            <button
              className="bg-gray-300 py-3 px-4 mr-3 flex justify-center basis-1/2 border-0 text-md rounded-md font-semibold transition-all hover:bg-gray-400"
              onClick={() => {
                favourites.setSelectable(false);
                favourites.deselectAll();
                myLocations.setSelectable(false);
                myLocations.deselectAll();
              }}
            >
              {t('gys-favourites:button-abort')}
            </button>
            <button
              className="bg-red-600 py-3 px-4 flex justify-center basis-1/2 border-0 text-md text-white rounded-md font-semibold transition-all hover:bg-red-700"
              onClick={() => {
                layers.openModal(Modals.confirmDelete);
              }}
            >
              {t('gys-favourites:button_delete_seletion')}
            </button>
          </div>
        ) : null}

        {config.features.myLocations ? (
          <>
            {myLocations?.list?.length !== 0 ? (
              myLocations.list.map((item) => (
                <div className="flex justify-center flex-col items-center w-full mt-10">
                  <div className="font-semibold text-xl mb-2 text-neutral-900">
                    {t('gys-myLocations:layer-title')}
                  </div>
                  <Favorite
                    poi={item}
                    language={config.gysService.language}
                    key={item.id}
                    onClick={flyTo.bind(null, item)}
                    selectable={myLocations.selectable}
                    selected={
                      myLocations.selected.filter((fav) => fav.id === item.id)
                        .length > 0
                    }
                    onSeletionChanged={(event) => {
                      if (event.target.checked) {
                        myLocations.select(item);
                      } else {
                        myLocations.deselect(item);
                      }
                    }}
                    description={item.de?.description}
                  />
                </div>
              ))
            ) : (
              <div className="flex justify-center flex-col items-center mt-10 w-full">
                <div className="font-semibold text-xl mb-2 text-neutral-900">
                  {t('gys-myLocations:layer-title')}
                </div>
                <p className="text-neutral-600 text-center">
                  {t('gys-myLocations:layer-empty-locations')}
                </p>
                <div className="mt-10 w-full">
                  <img
                    className="w-16 h-auto mx-auto -mb-4 animate-bounce"
                    src="../add-marker.svg"
                    alt=""
                  />
                  <img
                    className="max-w-full"
                    src="../add-marker-bg.svg"
                    alt=""
                  />
                </div>
              </div>
            )}
            <button
              className="bg-neutral-200 py-2 px-4 mt-10 rounded-3xl text-sm hover:bg-neutral-300 transition-all w-full"
              onClick={() => {
                layers.closeLayer();
                layers.openModal(Modals.addLocationHelp);
              }}
            >
              <span className="icon icon-plus"></span>{' '}
              {t('gys-myLocations:layer-add-custom-location')}
            </button>
          </>
        ) : null}
      </div>
    </SidebarPanel>
  );
});
